/* tslint:disable:variable-name */
import {Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, SkipSelf, ViewChild} from '@angular/core';
import {Bucket, Token} from '@paperlessio/sdk/api/models'
import {DropdownItem} from '@blocks/rich-text/dropdown-item';
import {EDITOR_HEADERS, EDITOR_SIZES} from '@blocks/rich-text/rich-text.consts';
import {DropdownComponent} from '@paperlessio/sdk/components/dropdown';
import {SearchComponent} from '@paperlessio/sdk/components/search';
import {BlockStore} from '@blocks/block/block.store';
import {TokenStore} from '@management/editor/tokens/token.store';
import {Router} from '@angular/router';
import {RichTextService} from '@blocks/rich-text/rich-text.service';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {BlockComponent} from '@blocks/block/block.component';
import {Subscription} from 'rxjs';
import {BucketStore} from '@management/base/bucket/bucket.store';

const shortcutKey = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform) ? 'cmd' : 'ctrl'

export class RichTextToolbarState {
  constructor(v: Partial<RichTextToolbarState>) {
    Object.assign(this, v);
  }

  header: DropdownItem;
  font: 'sans-serif';
  size: string;
  bold = false;
  italic = false;
  underline = false;
  strike = false;
  align: 'center' | 'right';
  color: string;
  background: string;
  list: 'bullet' | 'ordered';
  indent = 0;
  script: 'super' | 'sub';
  link: string;
  blockquote = false;
  presets: string[];

  static fromQuillFormat(format: any) {
    return new RichTextToolbarState({
      header: format.header && EDITOR_HEADERS.find(header => header.value === format.header),
      font: format.font,
      size: format.size,
      bold: !!format.bold,
      italic: !!format.italic,
      underline: !!format.underline,
      strike: !!format.strike,
      color: format.color,
      background: format.background,
      list: format.list,
      indent: parseInt(format.indent || 0, 10),
      align: format.align,
      link: format.link,
      script: format.script,
      blockquote: !!format.blockquote
    });
  }
}

@Component({
  selector: 'app-rich-text-toolbar',
  templateUrl: './rich-text-toolbar.component.html',
  styleUrls: ['./rich-text-toolbar.component.scss']
})
export class RichTextToolbarComponent implements OnInit, OnDestroy {
  constructor(@Optional() @SkipSelf() private blockComponent: BlockComponent,
              private blockStore: BlockStore,
              private bucketStore: BucketStore,
              private blockMutationService: BlockMutationService,
              private tokenStore: TokenStore,
              private router: Router,
              public richTextService: RichTextService) {}

  private colorChangeTimeout: number | undefined;
  private backgroundChangeTimeout: number | undefined;
  private _selectedText = '';
  private subs = new Subscription();

  @ViewChild('variableDropdown') variableDropdownComponent: DropdownComponent;
  @ViewChild('colorDropdown') private colorDropdownComponent: DropdownComponent;
  @ViewChild('backgroundDropdown') private backgroundDropdownComponent: DropdownComponent;
  @ViewChild('searchVariable') public variableSearchComponent: SearchComponent;

  @Input() state: RichTextToolbarState;
  @Input() variables: Token[] = [];
  @Input() variableSearchQuery = '';
  @Input() showVariableButton = true;
  @Input() richTextBlockIds: number[];
  @Input() selectAll = false;

  get selectedText(): string {
    return this._selectedText;
  }

  @Input() set selectedText(value: string) {
    this._selectedText = value?.length > 2 ? value?.slice(0, 50) : '';
    this.mapVariables();
    value = value?.toLowerCase();
    this.selectedVariable = this.variablesMapped.find(
      v => v.name === value);
  }

  @Output() close = new EventEmitter<boolean>();

  public sizes: DropdownItem[] = EDITOR_SIZES;
  public headers: DropdownItem[] = EDITOR_HEADERS;
  public variablesMapped: {name: string, variable: Token}[] = [];
  public shortcutKey = shortcutKey;
  public selectedVariable: {name: string, variable: Token} | null = null;
  bucket: Bucket;
  bucketTypeNamespace: string;

  ngOnInit() {
    this.subs.add(this.bucketStore.bucket.subscribe(bucket => {
      this.bucket = bucket;
      this.bucketTypeNamespace = this.bucketStore.bucketTypeNamespace;
    }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  format(kind: string, value?: any) {
    this.richTextService.format(
      this.richTextBlockIds,
      kind,
      value,
      this.selectAll ? {index: 0, length: Number.MAX_SAFE_INTEGER} : null);

    if (this.richTextBlockIds.length > 1) {
      this.richTextService.blur();
    }
  }

  clean() {
    this.richTextService.clean(this.richTextBlockIds, this.selectAll ? {index: 0, length: Number.MAX_SAFE_INTEGER} : null);
  }

  addVariable() {
    if (!this.selectedText || this.selectedVariable) {
      this.router
        .navigate(['', {outlets: {modal: 'm/tokens/new'}}], {queryParamsHandling: 'preserve'})
        .then(_ => this.close.emit(true));
      return;
    }

    const token = new Token({
      bucket_id: this.blockStore.bucket_id,
      bucket_type: this.blockStore.bucket_type,
      name: this.selectedText
    });

    this.subs.add(this.tokenStore
      .create(token, {}, false, false)
      .subscribe(() => this.richTextService.insertVariable(this.richTextBlockIds[0], this.selectedText)));
  }

  insertVariable(variableName: string) {
    this.richTextService.insertVariable(this.richTextBlockIds[0], variableName);
  }

  mapVariables() {
    this.variablesMapped = this.variables.map(v => ({name: v.name.toLowerCase(), variable: v}));
  }

  colorChange(event: string) {
    this.format('color', event);
  }

  backgroundChange(event: string) {
    this.format('background', event);
  }
}
